<template>
    <div class="step-container" ref="stepContainer">
        <div class="align-middle step-title grid-x" @click="folded = !folded">
            <div class="cell auto">
                <p class="display-large semi-bold margin-0">{{ content.name }}</p>
                <p v-if="stepRequirements" class="display-small color-battleship-gray">{{ stepRequirements }}</p>
                <p v-if="help" class="display-small color-battleship-gray margin-0"><i>*{{ help }}</i></p>
            </div>
            <p v-if="isRequired" class="cell shrink margin-0 required-label bold text-uppercase">Required</p>
        </div>
        <div v-if="!folded">
            <ingredient-list :parent-offset="offsetTop" :type="selectionType" :step="content" :ingredients="ingredients"></ingredient-list>
            <product-option-input v-for="productOption in productOptions" :key="productOption.id" :step="content" :product-option="productOption"></product-option-input>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Step, { StepType } from '@/models/step';
import ProductCustomizerModule from '@/stores/modules/product-customizer';
import { getModule } from 'vuex-module-decorators';
import Chip from '@/components/common/ui/Chip.vue';
import Ingredient from '@/models/ingredient';
import IngredientList from './IngredientList.vue';
import ProductOptionInput from './ProductOptionInput.vue';
import { IngredientListType } from '@/utils/types';
import { Icon } from '@/front/icons';

@Component({
    components: { Chip, IngredientList, ProductOptionInput }
})
export default class CustomizationStep extends Vue
{
    @Prop(Object) readonly content!: Step;

    productCustomizerModule!: ProductCustomizerModule;
    offsetTop: number = 0;
    folded: boolean = false;

    created() {
        this.productCustomizerModule = getModule(ProductCustomizerModule, this.$store);
    }

    mounted() {
        this.offsetTop = <number>(<HTMLElement>this.$refs.stepContainer).getBoundingClientRect().top;
    }

    removeIngredient(ingredient: Ingredient) {
        this.productCustomizerModule.removeIngredient([this.content, ingredient]);
    }

    get selectedIngredientLabel() {
        return (ingredient: Ingredient) => {
            let ingredientLabel = ingredient.name;
            if (ingredient.quantifiable && ingredient.quantity! > 1) {
                ingredientLabel = ingredient.quantity + ' ' + ingredient.name;
            }
            if (!ingredient.in_stock) {
                ingredientLabel += ' (Out of stock)';
            }
            return ingredientLabel;
        }
    }

    get selectionType() {
        if (this.content.max_items === 1 && this.content.min_items === 1) return IngredientListType.SINGLE;
        return IngredientListType.MULTI;
    }

    get StepType() {
        return StepType;
    }

    get productOptions() {
        return this.productCustomizerModule.stepProductOptions(this.content);
    }

    get ingredients() {
        return this.content.ingredients;
    }

    get selectedIngredients() {
        return this.productCustomizerModule.stepIngredients(this.content);
    }

    get isRequired() {
        return this.content.min_items > 0;
    }

    get stepRequirements() {
        if (this.content.min_items > 1 && !this.content.max_items) {
            return `Pick at least ${this.content.min_items}`;
        } else if ((this.content.min_items > 1 || this.content.max_items > 1) && this.content.min_items && this.content.max_items) {
            if (this.content.min_items === this.content.max_items) {
                return `Pick exactly ${this.content.min_items}`;
            }
            return `Pick between ${this.content.min_items} and ${this.content.max_items}`;
        } else if (!this.content.min_items && this.content.max_items) {
            return `Choose up to ${this.content.max_items}`;
        }
        return '';
    }

    get help() {
        return this.content.help;
    }

    get Icon() {
        return Icon;
    }
}
</script>
<style lang="scss">
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.step-title {
    padding: 15px 25px 15px;
    margin-bottom: 0px;
    background-color: var(--almost-white);

    &.folded {
        margin-bottom: 5px;
    }

    .fold-icon {
        height: 30px;
    }

    .required-label {
        color: white;
        background-color: #BE2F37;
        border-radius: 1000px;
        padding: 2px 8px;
        font-size: 11px;
        line-height: 16px; /* 145.455% */
        letter-spacing: 1px;
    }
}

.step-container {
    position: relative;
    padding-top: 15px;

    &:hover {
        cursor: pointer;
    }
}

.ingredients {
    padding-top: 15px;

    .ingredient-chip {
        margin: 0 5px 5px 0;
    }
}

.step-actions {
    padding-top: 15px;

    .save-step-button {
        @include breakpoint(medium down) {
            position: fixed;
            bottom: 15px;
            width: calc(100% - 30px);
        }
    }
}

</style>
